/**
 * Toast Notification System
 * Requirements: 8.1, 8.2, 10.1, 10.2 - Unified API error notifications via Toast
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { Snackbar, Alert, AlertColor, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export interface ToastMessage {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number;
}

export interface ToastOptions {
  message: string;
  severity?: AlertColor;
  duration?: number;
}

export interface ToastContextValue {
  showToast: (options: ToastOptions) => void;
  showError: (message: string, duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export interface ToastProviderProps {
  children: ReactNode;
  defaultDuration?: number;
}

export const ToastProvider = ({
  children,
  defaultDuration = 5000,
}: ToastProviderProps) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const generateId = () => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const showToast = useCallback(
    (options: ToastOptions) => {
      const id = generateId();
      const toast: ToastMessage = {
        id,
        message: options.message,
        severity: options.severity || 'info',
        duration: options.duration ?? defaultDuration,
      };
      setToasts((prev) => [...prev, toast]);
    },
    [defaultDuration]
  );

  const showError = useCallback(
    (message: string, duration?: number) => {
      showToast({ message, severity: 'error', duration });
    },
    [showToast]
  );

  const showSuccess = useCallback(
    (message: string, duration?: number) => {
      showToast({ message, severity: 'success', duration });
    },
    [showToast]
  );

  const showWarning = useCallback(
    (message: string, duration?: number) => {
      showToast({ message, severity: 'warning', duration });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (message: string, duration?: number) => {
      showToast({ message, severity: 'info', duration });
    },
    [showToast]
  );

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const value: ToastContextValue = {
    showToast,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    hideToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={() => hideToast(toast.id)} />
      ))}
    </ToastContext.Provider>
  );
};

interface ToastItemProps {
  toast: ToastMessage;
  onClose: () => void;
}

const ToastItem = ({ toast, onClose }: ToastItemProps) => {
  const [open, setOpen] = useState(true);

  const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
    // Delay actual removal to allow for exit animation
    setTimeout(onClose, 200);
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={toast.duration}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Alert
        severity={toast.severity}
        variant="filled"
        onClose={handleClose}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        sx={{ width: '100%', minWidth: 300 }}
      >
        {toast.message}
      </Alert>
    </Snackbar>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Empty container for backwards compatibility (toasts are rendered in provider)
export const ToastContainer = () => null;

export default ToastProvider;
